import { ACCESS_TOKEN_REFRESH_INTERVAL } from 'config/constants';
import { formatDateTime } from 'helpers/dateHelper';
import { t } from 'i18next';
import {
  clearStoredUser,
  readStoredUser,
  setLoginGatePending,
  writeStoredUser,
} from 'lib/authSession';
import { applyPlayerTheme, readDeviceTheme } from 'lib/playerTheme';
import {
  refreshSessionTokens,
  SESSION_EXPIRED_EVENT,
  TOKENS_REFRESHED_EVENT,
} from 'lib/sessionTokens';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  loginApi,
  logoutApi,
  updateTheme as updateThemeApi,
} from 'services/api/authService';
import { BALANCE_STALE_EVENT, fetchBalance } from 'services/api/balanceService';
import { createWebSocket } from 'services/webSocketService';
import { UserInfo } from 'types/UserInfo';

export const UserContext = React.createContext(null as any);

/** Reconnect delays after a dropped socket (ms); the last one repeats. */
const RECONNECT_DELAYS = [1_000, 2_000, 5_000, 10_000, 30_000];

/** Focus / visibility re-reads of the balance are at most this frequent. */
const BALANCE_REFRESH_MIN_INTERVAL = 30_000;

export default ({ children }: any) => {
  const [loggedUser, setLoggedUser] = useState<UserInfo | null>(readStoredUser);
  const [webSocket, setWebSocket] = useState<WebSocket>(null!);

  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectAttemptRef = useRef(0);
  /** Newest ledger row reflected in the balance on screen. */
  const lastLedgerIdRef = useRef(0);
  const lastBalanceFetchRef = useRef(0);

  useEffect(() => {
    setLoginGatePending(!readStoredUser());
    const stored = readStoredUser();
    applyPlayerTheme(stored?.player_theme ?? readDeviceTheme());
  }, []);

  /**
   * Apply a balance from a websocket push or a snapshot. Each carries the
   * ledger row it reflects; anything older than what is already on screen is
   * dropped, so a late push cannot undo a fresher read.
   */
  const applyBalance = useCallback((balance: number, ledgerId?: number) => {
    if (!Number.isFinite(balance)) return;
    if (typeof ledgerId === 'number' && Number.isFinite(ledgerId)) {
      if (ledgerId < lastLedgerIdRef.current) return;
      lastLedgerIdRef.current = ledgerId;
    }
    setLoggedUser((prev) => {
      if (!prev || prev.balance === balance) return prev;
      const updated = { ...prev, balance };
      writeStoredUser(updated);
      return updated;
    });
  }, []);

  /**
   * Re-read the balance from the API. The websocket is the fast path, this is
   * the safety net for pushes that never arrived (sleeping tab, dropped
   * socket, server restart). `force` skips the throttle.
   */
  const refreshBalance = useCallback(
    async (force = false) => {
      if (!readStoredUser()) return;
      const now = Date.now();
      if (
        !force &&
        now - lastBalanceFetchRef.current < BALANCE_REFRESH_MIN_INTERVAL
      ) {
        return;
      }
      lastBalanceFetchRef.current = now;
      const snapshot = await fetchBalance();
      if (snapshot) applyBalance(snapshot.balance, snapshot.ledger_id);
    },
    [applyBalance]
  );

  const clearReconnectTimer = () => {
    if (reconnectTimerRef.current != null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  const closeSocket = useCallback(() => {
    clearReconnectTimer();
    const socket = socketRef.current;
    socketRef.current = null;
    if (socket) socket.close();
    setWebSocket(null!);
  }, []);

  /**
   * Open the player's socket and keep it open: when it drops, open a new one
   * after a growing delay. Consumers get the new socket through context and
   * re-subscribe; the server answers the auth message with a fresh balance.
   */
  const connectSocket = useCallback(
    (user: UserInfo) => {
      clearReconnectTimer();
      const socket = createWebSocket(
        user.id,
        () => readStoredUser()?.access_token
      );

      socket.addEventListener('open', () => {
        reconnectAttemptRef.current = 0;
      });

      socket.addEventListener('message', (e: MessageEvent) => {
        let data: any;
        try {
          data = JSON.parse(e.data);
        } catch {
          return;
        }
        if (data?.type === 'balance') {
          applyBalance(Number(data.balance), data.ledger_id);
        }
      });

      socket.addEventListener('close', () => {
        // Ignore sockets we replaced or closed on purpose.
        if (socketRef.current !== socket) return;
        socketRef.current = null;
        const attempt = reconnectAttemptRef.current;
        const delay =
          RECONNECT_DELAYS[Math.min(attempt, RECONNECT_DELAYS.length - 1)];
        reconnectAttemptRef.current = attempt + 1;
        reconnectTimerRef.current = window.setTimeout(() => {
          const current = readStoredUser();
          if (current) connectSocket(current);
        }, delay);
      });

      socketRef.current = socket;
      setWebSocket(socket);
    },
    [applyBalance]
  );

  const loggedUserId = loggedUser?.id;

  // One socket per logged-in player, opened on login and on page load.
  useEffect(() => {
    if (loggedUserId == null) return;
    const user = readStoredUser();
    if (user) connectSocket(user);
    return () => closeSocket();
  }, [loggedUserId, connectSocket, closeSocket]);

  // The balance saved in the tab can be hours old: read it on load and
  // whenever the player comes back to the tab or the network returns.
  useEffect(() => {
    if (loggedUserId == null) return;
    lastLedgerIdRef.current = 0;
    refreshBalance(true);

    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshBalance();
    };
    const onFocus = () => refreshBalance();
    const onOnline = () => refreshBalance(true);
    const onStale = () => refreshBalance(true);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    window.addEventListener(BALANCE_STALE_EVENT, onStale);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
      window.removeEventListener(BALANCE_STALE_EVENT, onStale);
    };
  }, [loggedUserId, refreshBalance]);

  const logout = useCallback(
    async (options?: { sessionExpired?: boolean }) => {
      const user = readStoredUser();
      if (user && !options?.sessionExpired && !(await logoutApi(user))) {
        alert(t('network_error_message'));
      }
      closeSocket();
      clearStoredUser();
      lastLedgerIdRef.current = 0;
      // The login screen keeps the palette the player just had.
      applyPlayerTheme(readDeviceTheme());
      setLoggedUser(null);
      setLoginGatePending(true);
    },
    [closeSocket]
  );

  // Keep the access token fresh. This used to start only on login, so a
  // reloaded tab never refreshed its token.
  useEffect(() => {
    if (loggedUserId == null) return;
    const interval = window.setInterval(async () => {
      if (!readStoredUser()) return;
      const refreshed = await refreshSessionTokens();
      if (!refreshed) logout();
    }, ACCESS_TOKEN_REFRESH_INTERVAL);
    return () => window.clearInterval(interval);
  }, [loggedUserId, logout]);

  // Tokens refreshed elsewhere (a request retried after a 401) and a session
  // that could not be refreshed at all.
  useEffect(() => {
    const onRefreshed = (e: Event) => {
      const updated = (e as CustomEvent<UserInfo>).detail;
      setLoggedUser((prev) =>
        prev
          ? {
              ...prev,
              access_token: updated.access_token,
              refresh_token: updated.refresh_token,
            }
          : prev
      );
    };
    const onExpired = () => {
      logout({ sessionExpired: true });
    };
    window.addEventListener(TOKENS_REFRESHED_EVENT, onRefreshed);
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => {
      window.removeEventListener(TOKENS_REFRESHED_EVENT, onRefreshed);
      window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
    };
  }, [logout]);

  const login = async (
    username: string,
    password: string
  ): Promise<UserInfo | null> => {
    const loginResult = await loginApi(username, password);
    if (loginResult) {
      loginResult.logged_in_from = formatDateTime(new Date());
      applyPlayerTheme(loginResult.player_theme);
      writeStoredUser(loginResult, true);
      lastLedgerIdRef.current = 0;
      setLoggedUser(loginResult);
      setLoginGatePending(false);
      return loginResult;
    }
    return null;
  };

  const updateTheme = async (playerTheme: string): Promise<boolean> => {
    if (!loggedUser) return false;
    const ok = await updateThemeApi(playerTheme, loggedUser);
    if (ok) {
      const updatedUser = { ...loggedUser, player_theme: playerTheme };
      writeStoredUser(updatedUser);
      setLoggedUser(updatedUser);
      applyPlayerTheme(playerTheme);
    }
    return ok;
  };

  return (
    <UserContext.Provider
      value={{
        loggedUser,
        login,
        logout,
        updateTheme,
        webSocket,
        refreshBalance,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
